import { CloudRpcError } from "../cloud/client.ts";
import type { InterfacePageCalls, MemberInterfacePages } from "../cloud/interface-pages.ts";

/* Which pages the company shows (Interfaces C6a; supabase/CLIENT-CONTRACT.md "Pages"). They are read when the company
 * opens, when the window comes back into focus, each minute while it shows, and after Settings switches one. A cloud
 * without the calls, or a member the calls don't answer, publishes nothing, so no page shows. A read that fails keeps
 * what was read before. */

export type InterfacePagesState = MemberInterfacePages;

const POLL_MS = 60_000;

export class CompanyInterfacePages {
	readonly #calls: InterfacePageCalls;
	readonly #publish: (state: InterfacePagesState | undefined) => void;
	readonly #stop = new AbortController();
	#poll?: ReturnType<typeof setInterval>;
	/** The read on its way. */
	#reading: Promise<void> | null = null;
	/** The read asked for while one was on its way: it runs once that one is answered, for every call meanwhile. */
	#again: Promise<void> | null = null;
	/** What was last published, as JSON; nothing is published while it stays the same. */
	#shown: string | undefined;

	constructor(
		calls: InterfacePageCalls,
		publish: (state: InterfacePagesState | undefined) => void,
		signal: AbortSignal,
	) {
		this.#calls = calls;
		this.#publish = publish;
		signal.addEventListener("abort", () => this.dispose(), { once: true, signal: this.#stop.signal });
	}

	/** Reads the pages, then follows focus and the minute poll. */
	open(): void {
		if (this.#stop.signal.aborted || this.#poll) return;
		const listen = { signal: this.#stop.signal };
		const shows = () => document.visibilityState === "visible";
		window.addEventListener("focus", () => void this.refresh(), listen);
		document.addEventListener("visibilitychange", () => void (shows() && this.refresh()), listen);
		this.#poll = setInterval(() => void (shows() && this.refresh()), POLL_MS);
		void this.refresh();
	}

	dispose(): void {
		if (this.#stop.signal.aborted) return;
		this.#stop.abort();
		clearInterval(this.#poll);
	}

	refresh(): Promise<void> {
		if (this.#stop.signal.aborted) return Promise.resolve();
		if (this.#reading) {
			const next = () => {
				this.#again = null;
				return this.refresh();
			};
			this.#again ??= this.#reading.then(next, next);
			return this.#again;
		}
		this.#reading = this.#read().finally(() => {
			this.#reading = null;
		});
		return this.#reading;
	}

	async #read(): Promise<void> {
		let state: InterfacePagesState | undefined;
		try {
			state = await this.#calls.list();
		} catch (error) {
			// Not deployed, or not answered for this member: there are no pages to show. Anything else keeps the last.
			if (!(error instanceof CloudRpcError && (error.missing || error.denied))) return;
			state = undefined;
		}
		if (this.#stop.signal.aborted) return;
		const shown = JSON.stringify(state ?? null);
		if (shown === this.#shown) return;
		this.#shown = shown;
		this.#publish(state);
	}
}
